import http from "@/http";
import { blobErrorMessage } from "@/utils/common";

// Mahalla jamg'armasi bildirishnomalari — admin qismi (spec: ADMIN.md).
// Spec pathlari `/v1/...` ko'rinishida yozilgan; bu consoleda barcha endpoint
// `api/v1/...` ostida (http instance baseURL = VUE_APP_BASE_API_URL).
const SETTINGS_BASE = "api/v1/admin/notification-settings";
const FUND_BASE = "api/v1/admin/quarter-fund";

export const DEFAULT_LIMIT = 50;

export default {
  // Uchta yozuv, id bo'yicha tartiblangan.
  settings() {
    return http.get(SETTINGS_BASE);
  },
  // Qismli update: faqat o'zgargan flag yuboriladi, qolganiga tegilmaydi —
  // shunda parallel o'zgarish bosib ketilmaydi. Javob bitta obyekt (ro'yxat emas).
  updateSetting(key, patch) {
    return http.patch(`${SETTINGS_BASE}/${encodeURIComponent(key)}`, patch);
  },
  subscribers(params) {
    return http.get(`${FUND_BASE}/subscribers`, { params });
  },
  // limit/offset e'tiborga olinmaydi — filtr bo'yicha to'liq ro'yxat (max 100 000).
  subscribersExcel(params) {
    return http.get(`${FUND_BASE}/subscribers/excel`, {
      params,
      responseType: "blob",
    });
  },
  notifications(params) {
    return http.get(`${FUND_BASE}/notifications`, { params });
  },
};

// ---- Enum'lar ----

export const EVENT_TYPES = [
  { value: "MONTHLY_BALANCE", label: "Oylik qoldiq" },
  { value: "INCOME", label: "Tushum" },
  { value: "OUTCOME", label: "Xarajat" },
];

// Navbat turmaydi: qator yaratish va yuborish bitta amal, shuning uchun
// `pending`/`processing` uzoq turib qolsa — nosozlik belgisi (ADMIN.md §3.5.1).
const STUCK_HINT =
  "Navbat turmaydi — uzoq shu holatda qolgan yozuv nosozlik belgisi";

export const NOTIFICATION_STATUSES = [
  { value: "sent", label: "Yuborilgan", tag: "success" },
  { value: "pending", label: "Navbatda", tag: "warning", hint: STUCK_HINT },
  { value: "processing", label: "Yuborilmoqda", tag: "warning", hint: STUCK_HINT },
  { value: "failed", label: "Xato", tag: "danger" },
  { value: "skipped", label: "O'tkazilgan", tag: "info" },
];

export const CHANNELS = [
  { key: "sms", flag: "sms_enabled", label: "SMS" },
  { key: "push", flag: "push_enabled", label: "Push" },
  { key: "inapp", flag: "inapp_enabled", label: "Ilovada" },
];

// Har bir kanal uchun alohida natija. Kanal `channels` obyektida umuman
// bo'lmasa — fuqaro bu kanalni yoqmagan.
const CHANNEL_STATUSES = {
  sent: {
    label: "ketdi",
    tag: "success",
    hint: "Navbatga qo'yildi — provayder yetkazgani emas",
  },
  failed: { label: "xato", tag: "danger", hint: "Sabab — qatorni ochib ko'ring" },
  skipped: { label: "o'tkazildi", tag: "warning" },
  disabled: {
    label: "o'chirilgan",
    tag: "info",
    hint: "Admin bu kanalni o'chirgan",
  },
};

// `skipped` sababi kanalga bog'liq: push uchun qurilma ro'yxatdan o'tmagan.
export function channelStatusMeta(channelKey, status) {
  const meta = CHANNEL_STATUSES[status] || { label: status, tag: "info" };
  const hint =
    status === "skipped" && channelKey === "push"
      ? "Ro'yxatga olingan qurilma yo'q"
      : meta.hint;
  return { label: meta.label, tag: meta.tag, hint: hint || "" };
}

// `last_error` inglizcha keladi: `skipped` uchun sabab, `failed` uchun kanal
// xatolari (`sms: ...; push: all tokens failed`) yoki panic (ADMIN.md §3.2).
const ERROR_REASONS = {
  "notification type disabled by administrator": "Admin bu turni o'chirgan",
  "recipient not found or deleted": "Foydalanuvchi o'chirilgan",
  "mahalla not found": "Mahalla topilmadi",
  "mahalla is not active": "Mahalla faol emas",
  "no channel enabled for this recipient": "Fuqaro hech qanday kanal yoqmagan",
  "all tokens failed": "Qurilma tokenlari ishlamadi",
  "panic during delivery": "Yuborishda ichki xatolik",
};

export function eventTypeLabel(value) {
  return EVENT_TYPES.find((t) => t.value === value)?.label || value || "—";
}

export function statusMeta(value) {
  return (
    NOTIFICATION_STATUSES.find((s) => s.value === value) || {
      value,
      label: value || "—",
      tag: "info",
    }
  );
}

// `last_error` kompozit bo'lishi mumkin ("sms: ...; push: all tokens failed") —
// shuning uchun mos kelgan barcha sabablar yig'iladi.
export function explainLastError(lastError) {
  if (!lastError) return "";
  const value = String(lastError).trim();
  const matched = Object.keys(ERROR_REASONS)
    .filter((reason) => value.includes(reason))
    .map((reason) => ERROR_REASONS[reason]);
  return matched.length ? matched.join("; ") : value;
}

// ---- Formatlash ----

// title_lang bo'sh obyekt bo'lishi mumkin (bazadagi JSON buzilgan) — `key` fallback.
export function settingTitle(setting) {
  const lang = setting?.title_lang || {};
  return lang.uz_lat || lang.uz_cyr || lang.ru || lang.en || setting?.key || "—";
}

// `amount` va `balance_after` TIYINda keladi. `amount` MONTHLY_BALANCE uchun null.
export function formatSom(tiyin) {
  if (tiyin === null || tiyin === undefined || tiyin === "") return "—";
  const value = Number(tiyin);
  if (Number.isNaN(value)) return "—";
  const som = value / 100;
  const fixed = Number.isInteger(som) ? som.toFixed(0) : som.toFixed(2);
  const [integer, fraction] = fixed.split(".");
  const grouped = integer.replace(/\B(?=(\d{3})+(?!\d))/g, " ");
  return fraction ? `${grouped},${fraction}` : grouped;
}

// phone_number `users` jadvalidan xom holda keladi: 9 raqamli ham, 998 prefiksli
// 12 raqamli ham uchraydi (ADMIN.md §2.3).
export function normalizePhone(raw) {
  const digits = String(raw || "").replace(/\D/g, "");
  return digits.length === 12 && digits.startsWith("998") ? digits.slice(3) : digits;
}

// Backend `search` ni ILIKE bilan qidiradi, bazada raqam xom holda yotadi:
// formatlangan "+998 90 123 45 67" hech qachon mos kelmaydi. Raqamli so'rov
// raqamlargacha qisqartiriladi, F.I.O. esa o'zgarishsiz ketadi.
export function normalizeSearch(raw) {
  const value = String(raw || "").trim();
  if (!value || !/^[\d\s()+-]+$/.test(value)) return value;
  return normalizePhone(value) || value;
}

export function formatPhone(raw) {
  const digits = normalizePhone(raw);
  if (digits.length !== 9) return raw || "—";
  return `+998 ${digits.slice(0, 2)} ${digits.slice(2, 5)} ${digits.slice(5, 7)} ${digits.slice(7)}`;
}

// ---- Xato ----

// Xato formati README §5. Excel javobi blob bo'lgani uchun xato body ham Blob
// bo'lib keladi — blobErrorMessage uni parse qiladi.
export async function apiErrorMessage(error, fallback = "Kutilmagan xatolik yuz berdi") {
  const response = error?.response;
  if (!response) return "Serverga ulanib bo'lmadi";
  if (response.status === 403) return "Bu bo'lim faqat admin uchun";
  return (await blobErrorMessage(response, fallback)) || fallback;
}

// ---- Hudud filtrlari (regions -> districts -> quarters) ----
// Bu uchlik consoledagi boshqa filtrlar bilan bir xil endpointlardan keladi.

export function loadRegions() {
  return http.get("api/v1/regions").then(({ data }) => data?.regions || []);
}

export function loadDistricts(regionId) {
  const params = regionId ? { region_id: regionId } : {};
  return http.get("api/v1/districts", { params }).then(({ data }) => data?.districts || []);
}

export function loadQuarters({ regionId, districtId }) {
  const params = {};
  if (regionId) params.region_id = regionId;
  if (districtId) params.district_id = districtId;
  return http.get("api/v1/quarters", { params }).then(({ data }) => data?.quarters || []);
}
